import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Globe, Sparkles, Truck } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Seo } from "@/lib/seo";

export default function Gateway() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "All Items";
  const search = params.get("search") || "";
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    api.get("/categories", { params: { collection: "gateway" } })
      .then((r) => setCategories(["All Items", ...r.data]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = { collection: "gateway" };
    if (category && category !== "All Items") q.category = category;
    if (search) q.search = search;
    api.get("/products", { params: q })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, search]);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All Items") next.delete("category");
    else next.set("category", c);
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (searchInput) next.set("search", searchInput);
    else next.delete("search");
    setParams(next);
  };

  return (
    <div data-testid="other-partners-page">
      <Seo title="Other Partners — Shipped by our product partners" path="/other-partners" description="Curated products supplied and shipped directly by JohnnyMart's trusted partner warehouses, backed by the Gorilla Guarantee." />
      {/* Immersive hero */}
      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #f2b134 0, transparent 40%), radial-gradient(circle at 80% 0, #b5231d 0, transparent 35%)" }} />
        <div className="jm-container relative py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-gold text-xs font-bold uppercase tracking-[0.2em]">
            <Globe className="w-4 h-4" /> Other Partners
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.05] max-w-3xl">
            Shipped by our <span className="text-gold">product partners.</span>
          </h1>
          <p className="mt-5 text-cream/75 text-base md:text-lg max-w-2xl leading-relaxed">
            These products are supplied and shipped directly by JohnnyMart's trusted outside product
            partners — curated picks delivered to your door, backed by the same Gorilla Guarantee.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
            {[
              { icon: Sparkles, t: "Hand-picked", s: "Curated, never random" },
              { icon: Truck, t: "Shipped to you", s: "Straight to your door" },
              { icon: Globe, t: "Worldwide reach", s: "Finds you won't see elsewhere" },
            ].map((f) => (
              <div key={f.t} className="flex items-start gap-3 rounded-lg border border-cream/15 bg-cream/[0.04] p-3.5">
                <f.icon className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <div>
                  <div className="text-cream font-bold text-sm">{f.t}</div>
                  <div className="text-cream/60 text-xs">{f.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="jm-container py-12">
        <form onSubmit={submitSearch} className="flex items-center bg-white rounded-md border border-navy/20 px-4 h-12 max-w-xl">
          <Search className="w-5 h-5 text-navy/50" />
          <input
            data-testid="gateway-search-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search Other Partners..."
            className="flex-1 bg-transparent outline-none px-3"
          />
          <button data-testid="gateway-search-btn" className="bg-brick text-white font-bold uppercase text-sm rounded px-4 py-1.5 hover:bg-navy transition-colors">
            Search
          </button>
        </form>

        {categories.length > 1 && (
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`gateway-filter-${c}`}
                onClick={() => setCategory(c)}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                  category === c ? "bg-brick text-white" : "bg-white border border-navy/20 text-navy hover:border-brick"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="mt-12 text-center text-navy/50">Opening the gate...</p>
        ) : products.length === 0 ? (
          <div data-testid="gateway-empty" className="mt-16 text-center">
            <Globe className="w-12 h-12 text-navy/20 mx-auto" />
            <p className="font-display text-2xl text-navy mt-4">Our partners are stocking up</p>
            <p className="text-ink/60 mt-2">New partner-shipped finds are being curated. Check back soon.</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
